import { useState, useMemo, useEffect } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useDebounce } from "../../hooks/useDebounce";
import {
  Search,
  Filter,
  ArrowUpDown,
  MoreHorizontal,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Shield,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { User } from "../../services/userService";
import { useAuth } from "../../contexts/AuthContext";

// Best-effort E.164 → human-readable formatter.
// The dashboard does not depend on libphonenumber-js, so this is a simple
// "split the country code prefix from the rest" display helper.
const formatPhoneForDisplay = (
  phone: string | undefined | null,
): string => {
  if (!phone) return "";
  // Strip everything except digits and a leading '+'.
  const cleaned = phone.replace(/[^\d+]/g, "");
  // Try to match a 1-4 digit country code after '+'.
  const match = cleaned.match(/^\+(\d{1,4})(\d+)$/);
  if (!match) return phone;
  return `${match[1]} ${match[2]}`;
};

interface UserTableProps {
  users: User[];
  title?: string;
  description?: string;
  isLoading?: boolean;
  onAddClick?: () => void;
  onEditClick: (user: User) => void;
  onDeleteClick: (user: User) => void;
  showRole?: boolean;
  addButtonLabel?: string;
  initialRoleFilter?: string | "all";
  onSearchChange?: (value: string) => void;
  serverSideSearch?: boolean;
  onViewClick?: (user: User) => void;
  viewButtonLabel?: string;
  extraHeaderActions?: React.ReactNode;
  // Server-side pagination props
  totalItems?: number;
  serverPage?: number;
  serverTotalPages?: number;
  onPageChange?: (page: number) => void;
  serverSidePagination?: boolean;
  // Parent-controlled filter state (lifted from local state so exports can read it)
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  roleFilter?: string;
  onRoleFilterChange?: (role: string) => void;
}

type SortField =
  | "firstName"
  | "lastName"
  | "email"
  | "role"
  | "createdAt"
  | "updatedAt";
type SortOrder = "asc" | "desc";

export function UserTable({
  users,
  title = "Users",
  description = "Manage your users and their permissions.",
  isLoading,
  onAddClick,
  onEditClick,
  onDeleteClick,
  showRole = true,
  addButtonLabel = "Add new user",
  initialRoleFilter = "all",
  onSearchChange,
  serverSideSearch = false,
  onViewClick,
  viewButtonLabel,
  extraHeaderActions,
  // Server-side pagination
  totalItems: propTotalItems,
  serverPage = 1,
  serverTotalPages = 1,
  onPageChange,
  serverSidePagination = false,
  // Parent-controlled filter state
  statusFilter = "all",
  onStatusFilterChange,
  roleFilter = "all",
  onRoleFilterChange,
}: UserTableProps) {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  // roleFilter and statusFilter are now controlled by the parent via props,
  // so the table receives already-filtered data and exports can read the active state.
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Handle server-side search debounce effect
  useEffect(() => {
    if (serverSideSearch && onSearchChange) {
      onSearchChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, serverSideSearch, onSearchChange]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let result = users.filter((user) => {
      // If server-side search is enabled, we assume 'users' is already filtered by search term.
      // Role and status filters are applied server-side via parent state, so no client-side
      // filtering is needed for those.
      const matchesSearch = serverSideSearch
        ? true
        : (user.firstName?.toLowerCase() || "").includes(
            searchTerm.toLowerCase(),
          ) ||
          (user.lastName?.toLowerCase() || "").includes(
            searchTerm.toLowerCase(),
          ) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.username?.toLowerCase() || "").includes(
            searchTerm.toLowerCase(),
          );

      return matchesSearch;
    });

    result.sort((a, b) => {
      let valA: any = a[sortField] || "";
      let valB: any = b[sortField] || "";

      if (sortField === "createdAt" || sortField === "updatedAt") {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      } else if (typeof valA === "string") {
        valA = valA.toLowerCase();
        valB = (valB as string).toLowerCase();
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [
    users,
    searchTerm,
    sortField,
    sortOrder,
    serverSideSearch,
  ]);

  // Calculate pagination
  const totalItems = serverSidePagination ? (propTotalItems ?? users.length) : filteredAndSortedUsers.length;
  const totalPages = serverSidePagination ? serverTotalPages : Math.ceil(totalItems / pageSize);
  const paginatedUsers = serverSidePagination
    ? users // Server already paginated
    : filteredAndSortedUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (serverSidePagination && onPageChange) {
      onPageChange(page);
    } else {
      setCurrentPage(page);
    }
  };

  // Current page for UI
  const effectivePage = serverSidePagination ? serverPage : currentPage;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          {title}
        </h2>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full xl:w-[408px]">
          <Input
            placeholder="Search users"
            icon={<Search size={20} />}
            className="bg-white w-full"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              if (!serverSidePagination) {
                setCurrentPage(1); // Reset to first page for client-side pagination
              }
            }}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter size={16} /> Filter
              </Button>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-3 min-w-[240px] w-full sm:w-max animate-in fade-in zoom-in duration-150">
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                      Status
                    </p>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          onStatusFilterChange?.("all");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "all"
                            ? "bg-gray-100 font-bold"
                            : "text-gray-700"
                        }`}
                      >
                        All Status
                      </button>
                      <button
                        onClick={() => {
                          onStatusFilterChange?.("verified");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "verified"
                            ? "bg-green-50 font-bold text-green-600"
                            : "text-gray-700"
                        }`}
                      >
                        Verified
                      </button>
                      <button
                        onClick={() => {
                          onStatusFilterChange?.("unverified");
                          setIsFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          statusFilter === "unverified"
                            ? "bg-red-50 font-bold text-red-600"
                            : "text-gray-700"
                        }`}
                      >
                        Unverified
                      </button>
                    </div>

                    {showRole && initialRoleFilter === "all" && (
                      <>
                        <div className="border-t border-gray-100 my-2"></div>
                        <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                          Role
                        </p>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => {
                              onRoleFilterChange?.("all");
                              setIsFilterOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                              roleFilter === "all"
                                ? "bg-gray-100 font-bold"
                                : "text-gray-700"
                            }`}
                          >
                            All Roles
                          </button>
                          <button
                            onClick={() => {
                              onRoleFilterChange?.("admin");
                              setIsFilterOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                              roleFilter === "admin"
                                ? "bg-purple-50 font-bold text-purple-600"
                                : "text-gray-700"
                            }`}
                          >
                            Admin
                          </button>
                          <button
                            onClick={() => {
                              onRoleFilterChange?.("instructor");
                              setIsFilterOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                              roleFilter === "instructor"
                                ? "bg-blue-50 font-bold text-blue-600"
                                : "text-gray-700"
                            }`}
                          >
                            Instructor
                          </button>
                          <button
                            onClick={() => {
                              onRoleFilterChange?.("student");
                              setIsFilterOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                              roleFilter === "student"
                                ? "bg-green-50 font-bold text-green-600"
                                : "text-gray-700"
                            }`}
                          >
                            Student
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsFilterOpen(false);
                }}
              >
                <ArrowUpDown size={16} /> Sort By
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px] animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        handleSort("createdAt");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "createdAt" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Date Joined
                    </button>
                    <button
                      onClick={() => {
                        handleSort("firstName");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "firstName" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      First Name
                    </button>
                    <button
                      onClick={() => {
                        handleSort("email");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "email" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Email
                    </button>
                    {showRole && (
                      <button
                        onClick={() => {
                          handleSort("role");
                          setIsSortOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          sortField === "role" ? "bg-gray-100 font-bold" : ""
                        }`}
                      >
                        Role
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Primary button and extra actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {extraHeaderActions}
            {onAddClick && hasPermission('users:create') && (
              <Button
                variant="primary"
                size="lg"
                className="w-full sm:w-[180px] whitespace-nowrap h-[48px] sm:h-[44px] touch-target"
                onClick={onAddClick}
              >
                {addButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card className="p-0 border shadow-sm w-full overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th
                  className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[20%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("firstName")}
                >
                  Name{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "firstName"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th
                  className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[25%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("email")}
                >
                  Email{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "email"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%]">
                  Phone
                </th>
                {showRole && (
                  <th
                    className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[10%] cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("role")}
                  >
                    Role{" "}
                    <ArrowUpDown
                      size={14}
                      className={`inline ml-1 ${
                        sortField === "role"
                          ? "text-indigo-600"
                          : "text-gray-400"
                      }`}
                    />
                  </th>
                )}
                {showRole && hasPermission('users:read') && (
                  <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%]">
                    Permissions
                  </th>
                )}
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[10%]">
                  Status
                </th>
                <th
                  className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSort("createdAt")}
                >
                  Joined{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "createdAt"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th className="px-3 sm:px-6 py-4 w-[5%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={showRole ? (hasPermission('users:read') ? 8 : 7) : 6}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    Loading users...
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={showRole ? (hasPermission('users:read') ? 8 : 7) : 6}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    No users found matching your filters.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr
                    key={user._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.profilePicture ? (
                          <img
                            src={user.profilePicture}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                            {(
                              user.firstName?.[0] ||
                              user.username?.[0] ||
                              "?"
                            ).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-[#102040]">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797] font-medium">
                      {user.email}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797] font-medium">
                      {user.phoneNumber ? (
                        <span className="text-gray-700">
                          {formatPhoneForDisplay(user.phoneNumber)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {showRole && (
                      <td className="px-3 sm:px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                          ${
                            user.role === "admin"
                              ? "bg-purple-100 text-purple-800"
                              : user.role === "instructor"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                          }`}
                        >
                          {user.isSuperAdmin ? "Super Admin" : user.role}
                        </span>
                      </td>
                    )}
                    {showRole && hasPermission('users:read') && (
                      <td className="px-3 sm:px-6 py-4">
                        {user.role === "admin" ? (
                          user.isSuperAdmin ? (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-purple-700 bg-purple-50">
                              <Shield size={12} className="mr-1" />
                              All Permissions
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-gray-600 bg-gray-50">
                              {user.permissions?.length || 0} Permissions
                            </span>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 sm:px-6 py-4">
                      {user.isEmailVerified ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#E8F5EE] text-[#1B8754]">
                          <CheckCircle2 size={12} className="mr-1" />
                          verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FEECEC] text-[#FF4D49]">
                          <XCircle size={12} className="mr-1" />
                          unverified
                        </span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right relative">
                      <button
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenu?.id === user._id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: user._id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>

                      <TableActionMenu
                        isOpen={activeMenu?.id === user._id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        {hasPermission('users:update') && (
                          <button
                            onClick={() => {
                              onEditClick(user);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Edit2 size={14} className="text-blue-500" />
                            Edit User
                          </button>
                        )}
                        {onViewClick && (
                          <button
                            onClick={() => {
                              onViewClick(user);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Edit2
                              size={14}
                              className="text-gray-500 opacity-0"
                            />
                            {viewButtonLabel || "View"}
                          </button>
                        )}
                        {hasPermission('users:delete') && (
                          <button
                            onClick={() => {
                              onDeleteClick(user);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 size={14} />
                            Delete User
                          </button>
                        )}
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
            <span>
              Showing {Math.min((effectivePage - 1) * pageSize + 1, totalItems)}{" "}
              to {Math.min(effectivePage * pageSize, totalItems)} of {totalItems}{" "}
              users
            </span>
            <div className="flex gap-1 ml-4">
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={effectivePage === 1}
                onClick={() => handlePageChange(Math.max(1, effectivePage - 1))}
              >
                &lt; Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Simple pagination logic: show first 5 or window around current
                // For now just simpler list
                let p = i + 1;
                if (totalPages > 5 && effectivePage > 3) {
                  p = effectivePage - 2 + i;
                }
                if (p > totalPages) return null;

                return (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`px-3 py-1 rounded ${
                      effectivePage === p
                        ? "bg-gray-900 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={effectivePage === totalPages}
                onClick={() =>
                  handlePageChange(Math.min(totalPages, effectivePage + 1))
                }
              >
                Next &gt;
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
